import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { IGridifyQuery } from "models/common";

const GRIDIFY_SPECIAL_CHARACTERS = /([\\(),|/=!><^$*#;'"])/g;

const escapeGridifyValue = (value: string): string =>
  value.replace(GRIDIFY_SPECIAL_CHARACTERS, "\\$1");

const toGridifyCondition = (
  field: string,
  operator: string,
  value: string | string[],
): string => {
  const escapedValue = Array.isArray(value)
    ? value.map(escapeGridifyValue)
    : escapeGridifyValue(value);

  switch (operator) {
    case "contains":
      return `${field}=*${escapedValue}/i`;
    case "doesNotContain":
      return `${field}!*${escapedValue}/i`;
    case "equals":
      return `${field}=${escapedValue}/i`;
    case "doesNotEqual":
      return `${field}!=${escapedValue}/i`;
    case "startsWith":
      return `${field} ^ ${escapedValue}/i`;
    case "endsWith":
      return `${field}$${escapedValue}/i`;
    case "isEmpty":
      return `${field}=`;
    case "isNotEmpty":
      return `${field}!=`;
    case "is":
      return `${field}=${escapedValue}`;
    case "not":
      return `${field}!=${escapedValue}`;
    case "isAnyOf":
      if (Array.isArray(escapedValue) && escapedValue.length > 0) {
        return `(${escapedValue.map((v) => `${field}=${v}/i`).join("|")})`;
      }
      return `${field}=${escapedValue}`;
    case ">":
      return `${field}>${escapedValue}`;
    case "<":
      return `${field}<${escapedValue}`;
    default:
      return `${field}=${escapedValue}`;
  }
};

export const buildGridifyFilter = (model: GridFilterModel): string => {
  const delimiter = model.logicOperator === "or" ? "|" : ",";
  const isEmptyOperator = (op: string) =>
    op === "isEmpty" || op === "isNotEmpty";

  const isAnyOfOperator = (op: string) => op === "isAnyOf";

  const isValidAnyOf = (item: any) =>
    isAnyOfOperator(item.operator) &&
    Array.isArray(item.value) &&
    item.value.length > 0 &&
    item.value.some((v: any) => v != null && v !== "");

  const isValidRegular = (item: any) =>
    !isEmptyOperator(item.operator) &&
    !isAnyOfOperator(item.operator) &&
    item.value != null &&
    item.value !== "";

  const isValidItem = (item: any) =>
    isEmptyOperator(item.operator) ||
    isValidAnyOf(item) ||
    isValidRegular(item);

  const getValue = (item: any) =>
    isEmptyOperator(item.operator)
      ? ""
      : isAnyOfOperator(item.operator)
        ? item.value
        : item.value.toString();

  return model.items
    .filter(isValidItem)
    .map((item) =>
      toGridifyCondition(item.field, item.operator, getValue(item)),
    )
    .filter(Boolean)
    .join(delimiter);
};

export const buildOrderBy = (sortModel: GridSortModel): string | undefined => {
  if (!sortModel.length) return undefined;
  return sortModel
    .map((item) => `${item.field} ${item.sort ?? "asc"}`)
    .join(", ");
};

/**
 * Converts a MUI DataGrid pagination model into Gridify paging parameters.
 *
 * MUI's `paginationModel.page` is 0-based, Gridify's `page` is 1-based.
 * This helper is the single place where that off-by-one is applied.
 */
export const toGridifyPaging = (
  paginationModel: GridPaginationModel,
): Pick<IGridifyQuery, "page" | "pageSize"> => ({
  page: paginationModel.page + 1,
  pageSize: paginationModel.pageSize,
});

/**
 * Builds a combined filter string for Gridify API
 * @param searchText - The search text to filter across multiple fields
 * @param gridFilters - The DataGrid filter model
 * @param searchFields - Array of field names to search across (e.g., ["name", "email", "department"])
 * @returns Combined filter string in Gridify format
 */
export const buildCombinedFilter = (
  searchText: string,
  gridFilters: GridFilterModel,
  searchFields: string[],
): string => {
  const s = searchText.trim(); // Trim whitespace from search text
  const columnFilter = buildGridifyFilter(gridFilters);

  if (!s) return columnFilter;

  // Build search conditions: search text matches ANY of the searchable fields
  const searchConditions = searchFields
    .map((field) => `${field}=*${escapeGridifyValue(s)}/i`)
    .join("|");

  // If there are column filters, combine them with AND logic
  // Use parentheses to ensure proper precedence:
  // (searchField1=*text|searchField2=*text|...) AND (columnFilters)
  if (columnFilter) {
    return `(${searchConditions}), (${columnFilter})`;
  }

  return searchConditions;
};

export const buildGridifyQueryString = (query?: IGridifyQuery): string => {
  if (!query) return "";

  const params = new URLSearchParams();
  if (query.filter) params.set("filter", query.filter);
  if (query.orderBy) params.set("orderBy", query.orderBy);
  if (query.page !== undefined) params.set("page", query.page.toString());
  if (query.pageSize !== undefined)
    params.set("pageSize", query.pageSize.toString());

  const qs = params.toString();
  return qs ? `?${qs}` : "";
};
